import { type ReactNode } from "react";
import { toast as sonnerToast } from "sonner";

/**
 * Compatibility shim for the legacy `useToast()` / `toast()` API.
 * Internally routes to Sonner so every existing call site continues
 * to work unchanged — but toasts now render via Sonner (modern look,
 * dismiss actions, queue, stack animation, etc.).
 *
 * New code should prefer calling sonner directly:
 *   import { toast } from "sonner";
 *   toast.success("Saved");
 *   toast.error("Failed", { description: "..." });
 *
 * This shim exists so we don't have to edit 31 files in a single PR.
 */

export type ToastVariant = "default" | "destructive";

export interface ShimToastProps {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ShimToastReturn {
  id: string | number;
  dismiss: () => void;
  update: (props: ShimToastProps) => void;
}

function resolveMessage(props: ShimToastProps): ReactNode {
  // Sonner puts the main message first, description in options.
  // If caller passed only a description, promote it to the message.
  return props.title ?? props.description ?? "";
}

function resolveOptions(props: ShimToastProps) {
  const options: {
    description?: ReactNode;
    duration?: number;
    action?: { label: string; onClick: (event: unknown) => void };
  } = {};

  if (props.title && props.description) {
    options.description = props.description;
  }
  if (props.duration !== undefined) {
    options.duration = props.duration;
  }
  if (props.action) {
    options.action = {
      label: props.action.label,
      onClick: () => props.action?.onClick(),
    };
  }
  return options;
}

export function toast(props: ShimToastProps): ShimToastReturn {
  const message = resolveMessage(props);
  const options = resolveOptions(props);

  const id =
    props.variant === "destructive"
      ? sonnerToast.error(message, options)
      : sonnerToast(message, options);

  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next) => {
      sonnerToast.dismiss(id);
      toast(next);
    },
  };
}

export function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) =>
      toastId !== undefined
        ? sonnerToast.dismiss(toastId)
        : sonnerToast.dismiss(),
    // Legacy: the old API exposed an array of current toasts via
    // `const { toasts } = useToast()`. Sonner manages its own queue,
    // so we return an empty array. The only consumer was the old
    // Toaster component which is being removed in this PR.
    toasts: [] as Array<never>,
  };
}
